// 261102 E1-P2 — «Mục thừa của phần mới» ở ④ Chia tập (điểm dừng 2). Hợp đồng giao diện = mockups/261102-mockup-append-unused.html (14 state).
// Mọi quyết định chữ / trạng thái nằm ở appendUnusedView.ts (thuần, có jest); khóa chống gửi trùng + bỏ phản hồi cũ ở useAppendUnused.ts.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Checkbox, Tag, Tooltip, message } from 'antd';
import type { AppendUnusedItem } from '../../../services/api/adaptation';
import Button from './WbButton';
import { Pill, Strip } from './WbSlot';
import { askConfirm, handleAppendError } from './appendRunActions';
import {
  checkedAfterToggleAll, earlierOrphansText, earlierWhyText, filterUnused, itemWhyText, legacyProofText, overviewBlockView, referenceKindLabel, uniq, removalView, removeButtonView,
  removeConfirmCopy, removedSummary, selectedSafeIds, toggleUnchecked, unavailableView, unusedCounts, unusedErrorText, unusedLeadText, unusedPanelState, unusedStatusPill,
  isUnusedErrorCode, type UnusedFilter,
} from './appendUnusedView';
import { entityFocusTarget } from './entityTableView';
import { useAppendUnused } from './useAppendUnused';
import { useWorkbenchCtx } from './WorkbenchContext';

type Named = Record<string, { name: string; kind: 'character' | 'location' }>;
const kindText = (k: 'character' | 'location') => (k === 'character' ? 'Nhân vật' : 'Bối cảnh');

const SectionHead: React.FC<{ title: string }> = ({ title }) => <div className="au-sec-h"><span className="au-sec-t">{title}</span></div>;

const SkeletonTable: React.FC = () => (
  <div className="au-scroll"><table className="wb-rb-table au-table"><tbody>
    {[0, 1, 2, 3, 4].map((i) => (
      <tr key={i}><td className="wb-rb-tick"><span className="au-skel" style={{ width: 16, display: 'inline-block' }} /></td><td><div className="au-skel" style={{ width: 70 }} /></td><td><div className="au-skel" style={{ width: 200 }} /></td><td><div className="au-skel" style={{ width: 90 }} /></td><td><div className="au-skel" style={{ width: 260 }} /></td></tr>
    ))}
  </tbody></table></div>
);

interface TableProps {
  items: AppendUnusedItem[]; lock: boolean; unchecked: string[]; locked: boolean; nameOf: (id: string) => string;
  onToggle: (id: string) => void; onToggleAll: (checkAll: boolean) => void; onOpen: (it: AppendUnusedItem) => void;
}
const UnusedTable: React.FC<TableProps> = ({ items, lock, unchecked, locked, nameOf, onToggle, onToggleAll, onOpen }) => {
  const selected = selectedSafeIds(items, unchecked);
  const safeTotal = unusedCounts(items).SAFE;
  return (
    <div className={`au-scroll${items.length > 8 ? ' au-tall' : ''}`}>
      <table className="wb-rb-table au-table">
        <thead><tr>
          <th className="wb-rb-tick">
            <Checkbox aria-label="Chọn tất cả mục an toàn" title="Chọn tất cả mục an toàn" checked={!lock && safeTotal > 0 && selected.length === safeTotal} indeterminate={!lock && selected.length > 0 && selected.length < safeTotal}
              disabled={lock || locked || !safeTotal} onChange={(e) => onToggleAll(e.target.checked)} />
          </th>
          <th>Loại</th><th>Tên · mã</th><th>Trạng thái</th><th>Vì sao</th><th />
        </tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={6} className="wb-rb-sub" style={{ padding: 16 }}>Không có mục nào ở nhóm này.</td></tr> : items.map((it) => {
            const safe = it.status === 'SAFE';
            const pill = unusedStatusPill(it.status);
            const why = itemWhyText(it, { overviewLock: lock });
            return (
              <tr key={it.id} className={safe ? undefined : 'wb-rb-row-need'} data-id={it.id}>
                <td className="wb-rb-tick">
                  <Tooltip title={safe && !lock ? undefined : why}>
                    <span><Checkbox aria-label={`Chọn ${nameOf(it.id)}`} checked={safe && !lock && !unchecked.includes(it.id)} disabled={!safe || lock || locked} onChange={() => onToggle(it.id)} /></span>
                  </Tooltip>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{kindText(it.kind)}</td>
                <td><div className="au-name"><b>{nameOf(it.id)} <Tag color="gold">Mới</Tag></b><span><code className="au-id">{it.id}</code></span></div></td>
                <td><Pill tone={pill.tone}>{pill.text}</Pill></td>
                <td><div className="au-why">
                  {it.status === 'REFERENCED' && it.referenceKinds.length > 0 && <div className="au-refs">{uniq(it.referenceKinds.map(referenceKindLabel)).map((l) => <Tag key={l}>{l}</Tag>)}</div>}
                  {why}
                </div></td>
                <td style={{ textAlign: 'right' }}>{safe ? null : <Button size="small" type="link" onClick={() => onOpen(it)}>Mở trong bảng</Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AppendUnusedPanel: React.FC = () => {
  const { storyId, workbench, data, dataVersion, refreshAll, goTo } = useWorkbenchCtx();
  const enabled = workbench?.appendRun?.status === 'WAIT_EPISODE_PLAN';
  const p = useAppendUnused(storyId, enabled, dataVersion, refreshAll);
  const [filter, setFilter] = useState<UnusedFilter>('all');
  const [unchecked, setUnchecked] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const removedNames = useRef<Named>({});
  const view = p.view;
  const removalAt = view?.removal?.postModifiedAt;
  // Đổi truyện ⇒ quên tên đã nhớ + lựa chọn cũ; đổi dấu vết lần bỏ ⇒ quên «đã tải lại».
  useEffect(() => { removedNames.current = {}; setUnchecked([]); setFilter('all'); }, [storyId]);
  useEffect(() => { setAcknowledged(false); }, [removalAt]);

  const outline = data.record?.outline;
  const entityMap = useMemo(() => {
    const m: Named = {};
    (outline?.characters ?? []).forEach((c) => { m[c.id] = { name: c.localizedName || c.name || c.id, kind: 'character' }; });
    (outline?.locations ?? []).forEach((l) => { m[l.id] = { name: l.localizedName || l.name || l.id, kind: 'location' }; });
    return m;
  }, [outline]);
  const nameOf = (id: string) => entityMap[id]?.name || removedNames.current[id]?.name || id;

  const state = unusedPanelState({ enabled, view, loading: p.loading, busy: p.busy, error: p.error });
  if (state === 'hidden') return null;

  const fail = async (e: unknown) => {
    const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
    if (isUnusedErrorCode(code)) { message.error(unusedErrorText(code), 8); await refreshAll(); } else await handleAppendError(e, { goTo, refreshAll });
  };
  const retry = () => { p.reload(); };
  const scrollToBar = () => document.getElementById('append-run-bar')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const open = (it: AppendUnusedItem) => goTo(entityFocusTarget(it.kind, it.id));

  const shell = (children: React.ReactNode, busyAttr = false) => <section className="au-sec" aria-label="Mục thừa của phần mới" aria-busy={busyAttr || undefined}>{children}</section>;

  if (state === 'loading') {
    const text = p.busy === 'remove' ? 'Đang bỏ các mục đã chọn…' : p.busy === 'undo' ? 'Đang hoàn tác…' : 'Đang kiểm xem mục nào của phần mới không ai dùng… (chỉ đọc, không gọi AI)';
    return shell(<>
      <SectionHead title="Mục thừa của phần mới" />
      <p className="au-lead"><span className="au-spin" />{text}</p>
      <SkeletonTable />
      <div className="au-foot"><Button disabled>Bỏ 0 mục an toàn</Button><span className="wb-rb-sub">Chờ xong.</span></div>
    </>, true);
  }
  if (state === 'error') {
    return shell(<>
      <SectionHead title="Mục thừa của phần mới" />
      <Alert type="error" showIcon message="Chưa kiểm được mục thừa của phần mới"
        description={<>
          <span>Máy chủ không trả lời hoặc dàn tập vừa đổi giữa chừng nên chưa biết mục nào dùng được. Chưa có gì bị bỏ. Nút «Bỏ mục an toàn» tạm khoá vì chưa chứng minh được mục nào an toàn.</span>
          <span className="au-actions"><Button type="primary" cta onClick={retry}>Kiểm lại</Button><Button onClick={() => { refreshAll(); }}>Tải lại</Button></span>
        </>} />
    </>);
  }
  if (state === 'stage' || state === 'legacy') {
    const u = state === 'legacy' ? { title: 'Chưa bỏ an toàn được lần thêm chương này', text: legacyProofText, primary: 'retry' as const, secondary: undefined } : unavailableView(view?.reason);
    return shell(<>
      <SectionHead title="Mục thừa của phần mới" />
      <Alert type="warning" showIcon message={u.title}
        description={<>
          <span>{u.text}</span>
          <span className="au-actions">
            {u.primary === 'reload' ? <Button type="primary" cta onClick={() => { refreshAll(); }}>Tải lại</Button> : <Button type="primary" cta onClick={retry}>Kiểm lại</Button>}
            {u.secondary === 'bar' && <Button onClick={scrollToBar}>Xem dải Phần mới</Button>}
          </span>
        </>} />
    </>);
  }

  // Từ đây view.available === true.
  const v = view!;
  const items = v.candidates ?? [];
  const earlier = v.earlierOrphans ?? [];
  const counts = unusedCounts(items);
  const lock = v.overviewPresent;
  const selected = selectedSafeIds(items, unchecked);
  const rest = counts.REFERENCED + counts.UNKNOWN;
  const remove = removeButtonView(selected.length, { overviewPresent: lock, busy: !!p.busy, loading: p.loading, previewError: !!p.error, safeTotal: counts.SAFE });
  const block = overviewBlockView(lock);

  const doRemove = async () => {
    const ids = selected;
    ids.forEach((id) => { if (entityMap[id]) removedNames.current[id] = entityMap[id]; });
    try {
      const r = await p.remove(ids, () => askConfirm({ title: 'Bỏ mục thừa?', content: removeConfirmCopy(ids.map(nameOf)), okText: remove.text, danger: true }));
      if (r === 'done') { setUnchecked([]); setFilter('all'); message.success(`Đã bỏ ${ids.length} mục an toàn`); }
    } catch (e) { await fail(e); }
  };
  const doUndo = async () => {
    try { const r = await p.undo(); if (r === 'done') { setUnchecked([]); message.success('Đã hoàn tác — khung xương về đúng bản trước lúc bỏ; vẫn ở bước chốt dàn tập'); } } catch (e) { await fail(e); }
  };

  const earlierBlock = earlier.length > 0 && (
    <section className="au-sec" data-au="earlier">
      <SectionHead title={`Mục thừa từ các lần thêm chương trước (${earlier.length})`} />
      <p className="au-lead">{earlierOrphansText(earlier.length)}. Lần xử lý này không đụng tới nên không có ô tick và không có nút bỏ ở đây.</p>
      <div className="au-scroll"><table className="wb-rb-table au-table">
        <thead><tr><th>Loại</th><th>Tên · mã</th><th>Trạng thái</th><th>Vì sao</th><th /></tr></thead>
        <tbody>{earlier.map((it) => (
          <tr key={it.id}>
            <td style={{ whiteSpace: 'nowrap' }}>{kindText(it.kind)}</td>
            <td><div className="au-name"><b>{nameOf(it.id)}</b><span><code className="au-id">{it.id}</code></span></div></td>
            <td><Pill tone="todo">{unusedStatusPill('OLD').text}</Pill></td>
            <td><div className="au-why">{earlierWhyText}</div></td>
            <td style={{ textAlign: 'right' }}><Button size="small" type="link" onClick={() => open(it)}>Mở trong bảng</Button></td>
          </tr>
        ))}</tbody>
      </table></div>
    </section>
  );

  const listBlock = items.length > 0 && (
    <>
      {block && (
        <Alert type="warning" showIcon style={{ margin: '6px 0' }} message={block.title}
          description={<><span>{block.text}</span><span><Button type="primary" cta onClick={() => goTo(block.target)}>{block.buttonText}</Button></span></>} />
      )}
      <div className="au-sum">
        <Pill tone="ok">{counts.SAFE} an toàn để bỏ</Pill>
        {counts.REFERENCED > 0 && <Pill tone="warn">{counts.REFERENCED} còn tham chiếu</Pill>}
        {counts.UNKNOWN > 0 && <Pill tone="todo">{counts.UNKNOWN} chưa chắc</Pill>}
      </div>
      <div className="wb-rb-tabs" role="tablist" aria-label="Lọc mục thừa">
        {([['all', 'Tất cả', counts.all], ['SAFE', 'An toàn', counts.SAFE], ['REFERENCED', 'Còn tham chiếu', counts.REFERENCED], ['UNKNOWN', 'Chưa chắc', counts.UNKNOWN]] as Array<[UnusedFilter, string, number]>).map(([f, label, n]) => (
          <button key={f} type="button" role="tab" aria-selected={filter === f} className={`wb-rb-tab${filter === f ? ' wb-rb-tab-on' : ''}`} onClick={() => setFilter(f)}>{label} <span className="wb-rb-tab-n">{n}</span></button>
        ))}
      </div>
      <UnusedTable items={filterUnused(items, filter)} lock={lock} locked={!!p.busy || p.loading} unchecked={unchecked} nameOf={nameOf}
        onToggle={(id) => setUnchecked(toggleUnchecked(unchecked, id))} onToggleAll={(all) => setUnchecked(checkedAfterToggleAll(items, all))} onOpen={open} />
      <div className="au-foot">
        <Tooltip title={remove.enabled ? undefined : remove.reason}><span><Button type="primary" cta danger disabled={!remove.enabled} onClick={doRemove}>{remove.text}</Button></span></Tooltip>
        <span className="au-grow" />
        <span className="wb-rb-sub">{rest ? `${rest} mục còn lại máy không tự bỏ được — lý do ở từng dòng. Xử lý bằng «Nhờ AI sửa (tốn AI)» / «Phương án (tốn AI)» ở dòng cảnh báo phía trên — đó là đường Trợ lý có sẵn, bạn tự bấm.` : 'Bỏ xong là hết lỗi mục thừa của lần này.'}</span>
      </div>
    </>
  );

  if (state === 'removed' && v.removal) {
    const sum = removedSummary(v.removal.ids, removedNames.current);
    const rv = removalView({ modifiedAt: v.modifiedAt, removal: v.removal }, acknowledged, sum.known ? { characters: sum.characters, locations: sum.locations } : undefined);
    return shell(<>
      <SectionHead title={`Mục thừa của phần mới (${items.length} còn lại)`} />
      <Strip tone={rv.kind === 'fresh' || rv.kind === 'stale-reloaded' ? 'ok' : 'warn'} role={rv.kind === 'stale' ? 'alert' : 'status'}
        action={rv.canUndo ? <Button size="small" disabled={!!p.busy || p.loading} onClick={doUndo}>Hoàn tác</Button>
          : rv.showReload ? <Button size="small" disabled={p.loading} onClick={() => { setAcknowledged(true); p.reload(); }}>Tải lại</Button> : undefined}>
        {rv.text}
      </Strip>
      <details className="au-removed"><summary>Đã bỏ ({sum.rows.length}) — xem danh sách</summary>
        <ul>{sum.rows.map((r) => <li key={r.id}>{r.label} <code className="au-id">{r.id}</code></li>)}</ul>
      </details>
      {items.length > 0 ? <><p className="au-lead" style={{ marginTop: 12 }}>Còn {items.length} mục của lần này máy không tự bỏ được (còn tham chiếu hoặc chưa chắc). Chúng vẫn là lỗi cho tới khi bạn xử lý:</p>{listBlock}</>
        : <p className="au-lead" style={{ marginTop: 12 }}>Không còn mục thừa nào của lần này.</p>}
      {earlierBlock}
    </>);
  }
  if (state === 'empty') {
    return shell(<>
      <SectionHead title="Mục thừa của phần mới (0)" />
      <Strip tone="ok">Sau khi chia tập, phần mới không để lại nhân vật hay bối cảnh nào mà chưa tập nào dùng — không cần dọn gì. Bạn xem dàn tập rồi chốt như bình thường.</Strip>
      {earlierBlock}
    </>);
  }
  return shell(<>
    <SectionHead title={`Mục thừa của phần mới (${counts.all})`} />
    <p className="au-lead">{unusedLeadText(counts)}</p>
    {listBlock}
    {earlierBlock}
  </>);
};
export default AppendUnusedPanel;
